import { useTranslation } from 'react-i18next';
import { es, enUS } from 'date-fns/locale';
import { format } from 'date-fns';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import type { TimelineAbsenceBlock } from '../../types';

const ABSENCE_FILL = 'rgba(229, 191, 88, 0.55)';
const ABSENCE_BORDER = '#b48300';

interface TimelineAbsenceBarProps {
  block: TimelineAbsenceBlock;
  leftPx: number;
  widthPx: number;
}

/**
 * A contiguous run of absence days for one person. Visually distinct from a
 * task bar (dashed border, fixed amber fill) since it isn't a task — the
 * backend does not distinguish pending vs. approved absences, so both render
 * identically (see the timeline integration guide).
 */
export function TimelineAbsenceBar({ block, leftPx, widthPx }: TimelineAbsenceBarProps) {
  const { t, i18n } = useTranslation('tasks');
  const locale = i18n.language.startsWith('es') ? es : enUS;
  const startLabel = format(block.startDate, 'd MMM', { locale });
  const endLabel = format(block.endDate, 'd MMM', { locale });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          data-testid="tasks-timeline-absence-bar"
          className="absolute top-1/2 h-8 -translate-y-1/2 rounded-md border-2 border-dashed"
          style={{
            left: leftPx,
            width: widthPx,
            backgroundColor: ABSENCE_FILL,
            borderColor: ABSENCE_BORDER,
          }}
        />
      </TooltipTrigger>
      <TooltipContent>
        {t('timeline.absence.tooltip', { startDate: startLabel, endDate: endLabel })}
      </TooltipContent>
    </Tooltip>
  );
}
